import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  MagnifyingGlass as Search,
  BookOpen,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/shared/StatCard';
import { RowActions } from '@/components/shared/RowActions';
import { useJudgmentsStore } from '@/lib/judgmentsStore';
import { fetchJudgments as apiFetchJudgments, deleteJudgment } from '@/lib/judgmentsApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { useDebounce } from '@/lib/useDebounce';
import { useAuthStore } from '@/features/auth/store';

const PAGE_SIZE = 5;

export default function JudgmentsPage() {
  const navigate = useNavigate();
  // This page is rendered for admin AND clerk — navigate within the current role's tree,
  // never a hardcoded /admin (which is outside the clerk's routes and dead-ends).
  const memberType = useAuthStore((s) => s.user?.memberType);
  const basePath = memberType === 'senior_clerk' || memberType === 'junior_clerk' ? '/clerk' : '/admin';
  const storeJudgments = useJudgmentsStore((s) => s.judgments);
  const fetchJudgments = useJudgmentsStore((s) => s.fetchJudgments);
  const judgmentsLoading = useJudgmentsStore((s) => s.loading);
  const judgmentsError = useJudgmentsStore((s) => s.error);

  useEffect(() => { fetchJudgments(); }, [fetchJudgments]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [courtFilter, setCourtFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const searchQ = useQuery({
    queryKey: ['judgments-search', debouncedSearch],
    queryFn: () => apiFetchJudgments({ search: debouncedSearch }),
    enabled: debouncedSearch.length >= 2,
    staleTime: 30_000,
  });

  // Derive available courts from actual judgment data — no hardcoding
  const availableCourts = useMemo(() => {
    const all = [...storeJudgments, ...(searchQ.data ?? [])];
    return [...new Set(all.map(j => j.court).filter(Boolean))].sort();
  }, [storeJudgments, searchQ.data]);

  // RC-10: real stats derived from the loaded judgments — never hardcoded.
  const stats = useMemo(() => ({
    total: storeJudgments.length,
    won: storeJudgments.filter((j) => j.outcome === 'Won').length,
    publicCount: storeJudgments.filter((j) => j.isPublic).length,
    courts: new Set(storeJudgments.map((j) => j.court).filter(Boolean)).size,
  }), [storeJudgments]);

  // Filter judgments by search + court
  const filtered = useMemo(() => {
    const source = debouncedSearch.length >= 2 ? (searchQ.data ?? []) : storeJudgments;
    return source.filter((j) => {
      if (courtFilter && j.court !== courtFilter) return false;
      return true;
    });
  }, [debouncedSearch, searchQ.data, storeJudgments, courtFilter]);

  // Which source failed depends on what is on screen: a search draws from `searchQ`, everything
  // else from the store. Either way, "No judgments found" must never stand in for a failed fetch.
  const searching = debouncedSearch.length >= 2;
  const searchFailed = searching && searchQ.isError;
  const loadFailed = searching ? searchFailed : !!judgmentsError;
  // The tiles always count the store, never the search results, so they follow the store alone.
  const statsFailed = !!judgmentsError;

  // Reset to page 1 whenever filters change
  useMemo(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, courtFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const startIndex = (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Judgments"
        subtitle="Searchable database of judgments linked to your barristers"
        actions={
          <>
            <Button
              variant="primary"
              size="md"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate(`${basePath}/judgments/new`)}
            >
              Add judgment
            </Button>
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {/* An em dash, not a zero: a failed load must not read as "no judgments won". */}
        <StatCard label="Total judgments" value={statsFailed ? '\u2014' : String(stats.total)} hint={statsFailed ? 'could not load' : 'all-time'} />
        <StatCard label="Won"             value={statsFailed ? '\u2014' : String(stats.won)}   hint={statsFailed ? 'could not load' : 'recorded outcome'} />
        <StatCard label="Courts"          value={statsFailed ? '\u2014' : String(stats.courts)} hint={statsFailed ? 'could not load' : 'represented'} />
        <StatCard label="Public profile"  value={statsFailed ? '\u2014' : String(stats.publicCount)} hint={statsFailed ? 'could not load' : 'visible on website'} />
      </div>

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search citation, matter name, judge, keyword…"
              className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {searchQ.isFetching && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-card p-0.5 text-xs">
            <button
              onClick={() => setCourtFilter('')}
              className={`rounded-sm px-3 py-1.5 font-medium ${
                !courtFilter ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              All courts
            </button>
            {availableCourts.map((c) => (
              <button
                key={c}
                onClick={() => setCourtFilter(c)}
                className={`rounded-sm px-3 py-1.5 font-medium ${
                  courtFilter === c ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                {/* The leading cell holds the judgment icon, not an action — it was headed
                    "Actions" as well, so the table carried the same heading twice. */}
                <th className="w-10"></th>
                <th>Citation</th>
                <th>Matter</th>
                <th>Court</th>
                <th>Judge</th>
                <th>Practice area</th>
                <th>Barrister</th>
                <th>Outcome</th>
                <th>Date</th>
                <th>Pages</th>
                <th className="w-10 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadFailed ? (
                <tr>
                  <td colSpan={11} className="py-6">
                    <QueryErrorState
                      compact
                      what={searching ? 'the judgment search' : 'judgments'}
                      onRetry={searching ? () => void searchQ.refetch() : () => void fetchJudgments()}
                    />
                  </td>
                </tr>
              ) : judgmentsLoading && !searching && storeJudgments.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-sm text-muted-foreground">
                    Loading judgments…
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <BookOpen className="h-8 w-8" />
                      <p className="text-sm font-medium">No judgments found</p>
                      <p className="text-xs">Try adjusting your search or filters.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((j) => (
                  <tr
                    key={j.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`${basePath}/judgments/${j.id}`)}
                  >
                    <td>
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:text-brand-400">
                        <BookOpen className="h-4 w-4" />
                      </div>
                    </td>
                    <td className="text-xs text-brand-700 dark:text-brand-300">{j.citation}</td>
                    <td className="font-medium text-foreground">{j.case}</td>
                    <td className="text-muted-foreground">{j.court}</td>
                    <td className="text-muted-foreground">{j.judge}</td>
                    <td><Badge>{j.area}</Badge></td>
                    <td>{j.barrister}</td>
                    <td>
                      <Badge
                        variant={j.outcome === 'Won' ? 'success' : j.outcome === 'Lost' ? 'danger' : 'default'}
                      >
                        {j.outcome}
                      </Badge>
                    </td>
                    <td className="text-xs text-muted-foreground">{j.date}</td>
                    <td className="text-xs text-muted-foreground">{j.pages}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {/* RC-10: removed toast-only Download/Share (no real judgment PDF/share endpoint yet). */}
                        <RowActions
                          onView={() => navigate(`${basePath}/judgments/${j.id}`)}
                          onDelete={async () => {
                            try {
                              await deleteJudgment(j.id);
                              await fetchJudgments();
                              toast.success('Judgment deleted');
                            } catch {
                              toast.error('Could not delete judgment');
                            }
                          }}
                          deleteConfirm={{
                            title: 'Delete judgment?',
                            message: `Delete “${j.citation}” (${j.case})? This permanently removes the judgment record and cannot be undone.`,
                            confirmLabel: 'Delete',
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-muted-foreground">
          <div>
            {filtered.length > 0 ? (
              <>Showing <span className="text-foreground">{startIndex}–{endIndex}</span> of <span className="text-foreground">{filtered.length}</span> judgments</>
            ) : (
              <>No judgments to display</>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="xs"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="px-2 text-xs text-muted-foreground">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="xs"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
